// pages/Company/CompanyInviteAcceptPage.jsx
// ─────────────────────────────────────────────────────────────
// NEW — destination for "I was invited to a company" (from
// CompanyEntryPage) and for a direct invite-link landing
// (/business/invite/:token, e.g. clicked from an email).
//
// ⚠️ BACKEND CONTRACT ASSUMED, NOT YET BUILT. companyController.js's
// current addMember() requires the invitee to ALREADY have a platform
// account (User.findOne({email}) → 404 if not found) and adds them
// with status:"active" IMMEDIATELY — there's no pending/invited
// state, no token, and no acceptance step at all today. This page is
// built against the API shape such a flow WOULD need, clearly
// flagged here rather than silently assumed to exist:
//
//   GET  /mtrade/companies/invites/:token
//     → { status, data: { companyName, role, invitedEmail, expiresAt } }
//   POST /mtrade/companies/invites/:token/accept
//     → { status, message, data: { companyId } }
//
// This implies backend changes not yet built:
//   - CompanyMember needs an "invited" status (alongside active/removed)
//     and an invitedEmail field (so it works before the invitee has
//     even signed up), plus a token + expiresAt.
//   - addMember should CREATE an invite record instead of adding the
//     member directly — only flips to status:"active" once accepted.
//   - A new controller (inviteController.js?) + 2 routes for the
//     lookup/accept pair above.
//   - If the invitee has no account yet, the accept flow needs to
//     either redirect to signup-then-return, or this page needs an
//     inline signup step — NOT designed here, flagging it as the next
//     open question once the basic accept flow is confirmed.
//
// THIS PAGE WILL NOT FUNCTION until that backend work lands. Built
// now, frontend-first, per explicit instruction — treat as a working
// spec for the API to be built against, not as something to deploy
// yet.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../api/apiServices";
import { useAuth } from "../../context/useAuth";

const ROLE_LABELS = {
  admin:   "Admin — manage staff & run payroll",
  finance: "Finance — top up wallet, view payroll",
  staff:   "Staff — view-only access",
};

export default function CompanyInviteAcceptPage() {
  const { token: paramToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = paramToken || searchParams.get("token");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [invite, setInvite]   = useState(null);
  const [loading, setLoading] = useState(!!token); // no token = nothing to load yet, show the paste-link state immediately
  const [accepting, setAccepting] = useState(false);
  const [error, setError]     = useState(null);
  // FIXED — new. /business/join is now a legitimate landing page with
  // NO token (CompanyEntryPage's "I was invited" button goes here
  // directly), not just a symptom of a broken email link. Previously
  // any no-token visit showed a flat error ("ask your company admin
  // for a fresh invite link") which made no sense for someone who
  // deliberately clicked "I was invited" and simply hasn't pasted
  // their link in yet. This lets them paste/enter the link or token
  // they have, instead of dead-ending.
  const [pastedLink, setPastedLink] = useState("");

  // Pulls a token out of either a raw token string or a full pasted
  // URL (e.g. someone copy-pasting the whole email link rather than
  // just the token portion).
  const extractToken = (input) => {
    const trimmed = input.trim();
    try {
      const url = new URL(trimmed);
      return url.searchParams.get("token") || null;
    } catch {
      // Not a URL — treat the whole input as the raw token.
      return trimmed || null;
    }
  };

  const submitPastedLink = () => {
    const extracted = extractToken(pastedLink);
    if (!extracted) {
      toast.error("That doesn't look like a valid invite link");
      return;
    }
    navigate(`/business/join?token=${extracted}`, { replace: true });
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return; // no error — renders the paste-link state below instead
    }
    apiClient.get(`/mtrade/companies/invites/${token}`)
      .then(r => {
        if (r.data.status) setInvite(r.data.data);
        else setError(r.data.message || "This invite link is invalid or has expired.");
      })
      .catch(err => setError(err?.response?.data?.message || "This invite link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    if (!user) {
      // Not signed in — send to auth, then back here. Matches the
      // pattern other gated pages in this app already use
      // (useAuthStatus-style redirect-with-return).
      navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    setAccepting(true);
    try {
      const r = await apiClient.post(`/mtrade/companies/invites/${token}/accept`);
      if (r.data.status) {
        toast.success(`You've joined ${invite?.companyName}`);
        navigate("/business");
      } else {
        toast.error(r.data.message);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not accept invite");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
    </div>
  );

  // FIXED — new branch. No token at all (deliberate /business/join
  // visit, not a broken link) gets a real input to act on, instead of
  // being lumped into the same dead-end error state as an
  // invalid/expired token below.
  if (!token) return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-6 text-center font-display">
      <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center mb-4">
        <i className="ri-mail-open-line text-brand text-2xl" />
      </div>
      <h1 className="text-primary font-bold text-lg mb-1">Enter your invite link</h1>
      <p className="text-muted text-sm mb-6 max-w-xs">Paste the invite link or code your company admin sent you.</p>

      <div className="w-full max-w-sm space-y-3">
        <input
          value={pastedLink}
          onChange={e => setPastedLink(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submitPastedLink()}
          placeholder="Paste invite link or code"
          autoFocus
          className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3.5 text-sm text-primary outline-none focus:border-brand placeholder:text-faint"
        />
        <button
          onClick={submitPastedLink}
          disabled={!pastedLink.trim()}
          className="w-full bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-[0.98]"
        >
          Continue
        </button>
        <button onClick={() => navigate("/business/landing")} className="w-full text-muted text-xs font-semibold py-2">
          ← Back
        </button>
      </div>
    </div>
  );

  if (error || !invite) return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-6 text-center font-display">
      <div className="w-16 h-16 bg-danger-light rounded-2xl flex items-center justify-center mb-4">
        <i className="ri-link-unlink text-danger text-2xl" />
      </div>
      <h1 className="text-primary font-bold text-lg mb-1">Invite not found</h1>
      <p className="text-muted text-sm mb-6 max-w-xs">{error}</p>
      <button onClick={() => navigate("/business/landing")} className="text-brand text-sm font-semibold">Back to Company Home</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-6 text-center font-display">
      <div className="w-16 h-16 bg-brand-light rounded-2xl flex items-center justify-center mb-4">
        <i className="ri-mail-open-line text-brand text-2xl" />
      </div>
      <h1 className="text-primary font-bold text-xl mb-1">You're invited to join</h1>
      <p className="text-primary font-bold text-2xl mb-4">{invite.companyName}</p>

      <div className="bg-surface border border-border rounded-2xl px-5 py-4 max-w-sm w-full mb-6">
        <div className="flex justify-between text-sm py-2 border-b border-border">
          <span className="text-muted">Role</span>
          <span className="text-primary font-semibold text-right">{ROLE_LABELS[invite.role] || invite.role}</span>
        </div>
        <div className="flex justify-between text-sm py-2">
          <span className="text-muted">Invited email</span>
          <span className="text-primary font-semibold">{invite.invitedEmail}</span>
        </div>
      </div>

      {user && user.email !== invite.invitedEmail && (
        <div className="bg-warning-light border border-warning-border rounded-2xl px-4 py-3 max-w-sm w-full mb-6 flex items-start gap-2.5 text-left">
          <i className="ri-alert-line text-warning flex-shrink-0 mt-0.5" />
          <p className="text-warning-text text-xs leading-relaxed">
            You're signed in as <strong>{user.email}</strong>, but this invite was sent to <strong>{invite.invitedEmail}</strong>. Sign in with that email to accept.
          </p>
        </div>
      )}

      <button
        onClick={accept}
        disabled={accepting || (user && user.email !== invite.invitedEmail)}
        className="w-full max-w-sm bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-[0.98]"
      >
        {accepting ? "Joining…" : user ? "Accept & Join" : "Sign in to Accept"}
      </button>
    </div>
  );
}
